/**
 * Telling whether a supplier someone typed is one already on record.
 *
 * A supplier entered by hand is compared against the brewery's own suppliers and the catalog's
 * sellers before a new one is made, so "country malt group, inc." does not become a second Country
 * Malt Group beside the one its invoices are filed under (Tyler, 2026-09-28). The web app uses it to
 * suggest, and the API uses the same key to decide, so both agree on what counts as the same name.
 */

/** Words a company adds to its name that say nothing about which company it is. */
const CORPORATE = new Set([
  "inc",
  "incorporated",
  "llc",
  "ltd",
  "limited",
  "co",
  "company",
  "corp",
  "corporation",
  "plc",
  "gmbh",
]);

function wordsOf(name: string): string[] {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
}

/**
 * A company name reduced to what tells companies apart. Case, accents, punctuation and trademark
 * signs go, and so do corporate words at the end and "the" at the front: "The Country Malt Group,
 * Inc." and "country malt group" have the same key. Nothing else is dropped, so "Great Western
 * Malting" and "Great Western" stay different; an alias is how the catalog says they are one.
 */
export function companyKey(name: string): string {
  const words = wordsOf(name);
  while (words.length > 1 && CORPORATE.has(words[words.length - 1] as string)) words.pop();
  if (words.length > 1 && words[0] === "the") words.shift();
  return words.join(" ");
}

/** The initials of a company's name: "Brewers Supply Group" is "bsg". */
export function initialsOf(name: string): string {
  const key = companyKey(name);
  return key
    .split(" ")
    .filter((w) => w !== "and")
    .map((w) => w[0])
    .join("");
}

export interface SupplierCandidate {
  id: string;
  name: string;
  /** Other names it goes by, as the catalog records them ("CMG", "Country Malt"). */
  aliases?: readonly string[];
}

export interface SupplierMatch<C extends SupplierCandidate> {
  candidate: C;
  /**
   * The same company by any name it is known under: its name, an alias, or its initials. Entering
   * this name would make a duplicate, so the caller should use the candidate instead.
   */
  exact: boolean;
}

/**
 * Suppliers on record that a typed name could mean, best first.
 *
 * Exact, by name, alias or initials, comes before partial, where every typed word begins some word
 * of the candidate's name or an alias ("brewers sup" finds Brewers Supply Group). Candidates keep
 * their order within each group, so a caller that lists the brewery's own suppliers first keeps them
 * first. Initials need two letters or more, so "B" is not Brewers Supply Group.
 */
export function matchSuppliers<C extends SupplierCandidate>(
  typed: string,
  candidates: readonly C[],
  limit = 5,
): SupplierMatch<C>[] {
  const key = companyKey(typed);
  if (!key) return [];
  const typedWords = key.split(" ");
  const exact: SupplierMatch<C>[] = [];
  const partial: SupplierMatch<C>[] = [];
  for (const candidate of candidates) {
    const names = [candidate.name, ...(candidate.aliases ?? [])];
    const keys = names.map(companyKey);
    const initials = initialsOf(candidate.name);
    if (keys.includes(key) || (initials.length >= 2 && initials === key)) {
      exact.push({ candidate, exact: true });
      continue;
    }
    const partialHit = keys.some((k) => {
      const words = k.split(" ");
      return typedWords.every((t) => words.some((w) => w.startsWith(t)));
    });
    if (partialHit) partial.push({ candidate, exact: false });
  }
  return [...exact, ...partial].slice(0, limit);
}
